import React, { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import {
  FileText, Image as ImageIcon, Download, Loader2, FileUp, Printer, Paperclip, Clock,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const TYPE_CONFIG = {
  signed_copy: { label: "Signed Copy", color: "bg-emerald-100 text-emerald-700", icon: Printer },
  signing_pdf_generated: { label: "Signing PDF", color: "bg-cyan-100 text-cyan-700", icon: Printer },
  attachment: { label: "Attachment", color: "bg-slate-100 text-slate-600", icon: Paperclip },
};

const ICON_MAP = {
  "application/pdf": FileText,
  "image/jpeg": ImageIcon,
  "image/png": ImageIcon,
  "image/tiff": ImageIcon,
};

function formatBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return dateStr; }
}

export function DocumentsGallery({ contractId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/contracts/${contractId}/gallery`);
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    if (doc.type === "signing_pdf_generated") {
      toast.info("This is a record of PDF generation. Use 'Print & Sign' to re-download the signing PDF.");
      return;
    }
    try {
      const res = await apiClient.get(`/contracts/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download document");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-slate-400" size={24} />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-16" data-testid="gallery-empty">
        <FileUp size={40} className="mx-auto mb-3 text-slate-300" />
        <p className="text-slate-400 text-sm">No documents yet</p>
        <p className="text-slate-300 text-xs mt-1">Uploaded files and signed copies will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="documents-gallery">
      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-5 top-3 bottom-3 w-px bg-slate-200" />

        {documents.map((doc, idx) => {
          const typeConf = TYPE_CONFIG[doc.type] || TYPE_CONFIG.attachment;
          const IconComp = ICON_MAP[doc.content_type] || typeConf.icon;
          const isDownloadable = doc.type !== "signing_pdf_generated";

          return (
            <div
              key={doc.id || idx}
              className="relative flex items-start gap-4 py-3 pl-2"
              data-testid={`gallery-item-${idx}`}
            >
              {/* Timeline dot */}
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                doc.type === "signed_copy" ? "bg-emerald-100" :
                doc.type === "signing_pdf_generated" ? "bg-cyan-100" :
                "bg-slate-100"
              }`}>
                <IconComp size={14} className={
                  doc.type === "signed_copy" ? "text-emerald-600" :
                  doc.type === "signing_pdf_generated" ? "text-cyan-600" :
                  "text-slate-500"
                } />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 border border-slate-200 rounded-sm p-3 bg-white hover:border-slate-300 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">{doc.filename}</p>
                      <Badge className={`text-[10px] rounded-sm px-1.5 py-0 ${typeConf.color}`}>
                        {typeConf.label}
                      </Badge>
                      {doc.size_bytes > 0 && (
                        <span className="text-[10px] text-slate-400 font-mono">{formatBytes(doc.size_bytes)}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock size={10} /> {formatDate(doc.uploaded_at)}
                      </span>
                      <span>by {doc.uploaded_by_name}</span>
                    </div>
                    {doc.notes && (
                      <p className="text-xs text-slate-500 mt-1.5 italic">{doc.notes}</p>
                    )}
                  </div>

                  {isDownloadable && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 rounded-sm flex-shrink-0"
                      onClick={() => handleDownload(doc)}
                      data-testid={`download-doc-${idx}`}
                    >
                      <Download size={12} /> Download
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
